import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DATE_LOCALES, useI18n } from "@/lib/i18n";
import { imgUrl } from "@/lib/api";
import { useSettings, heroText } from "@/lib/settings";
import { BookingCalendar, useIsWide } from "./DatesSection";
import { GuestPicker } from "./GuestPicker";

const ease = [0.22, 1, 0.36, 1];

const Media = ({ hero, fallback }) => {
  const wide = useIsWide();
  const ref = useRef(null);
  const src = hero?.type === "video" ? imgUrl((!wide && hero.video_mobile) || hero.video) : "";
  useEffect(() => {
    if (ref.current) ref.current.load();
  }, [src]);
  if (src) {
    return (
      <video ref={ref} data-testid="hero-video" className="h-full w-full object-cover" autoPlay muted loop playsInline preload="auto" poster={imgUrl(hero.poster || hero.image || fallback)}>
        <source src={src} />
      </video>
    );
  }
  return <img data-testid="hero-image" src={imgUrl(hero?.image || fallback)} alt="Samara Beach House" className="h-full w-full object-cover" />;
};

const Line = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className="block"
      initial={{ y: "105%", letterSpacing: "0.32em", filter: "blur(10px)" }}
      animate={{ y: "0%", letterSpacing: "0.03em", filter: "blur(0px)" }}
      transition={{ duration: 1.6, delay: 0.5 + i * 0.18, ease }}
    >
      {text}
    </motion.span>
  </span>
);

const HeroBar = ({ b, onRequest }) => {
  const { t, lang } = useI18n();
  const wide = useIsWide(1100);
  const [dOpen, setDOpen] = useState(false);
  const f = (d) => (d ? format(d, "d MMM yyyy", { locale: DATE_LOCALES[lang] }) : t("w.add"));
  const cell = "flex-1 px-5 py-3 text-left transition-colors hover:bg-white/10";
  const label = "block font-mono text-[10px] uppercase tracking-[0.25em] text-gold";
  const guests = b.adults + b.children;
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 1, ease }} className="mx-auto w-full max-w-4xl" data-testid="hero-booking-bar">
      <p className="mb-3 text-center font-mono text-[11px] uppercase tracking-[0.35em] text-paper/90">{t("hb.stay")}</p>
      <div className="flex flex-col overflow-hidden rounded-2xl border border-white/25 bg-deep/45 text-paper backdrop-blur-xl md:flex-row md:rounded-full">
        <Popover open={dOpen} onOpenChange={setDOpen}>
          <PopoverTrigger asChild>
            <button data-testid="hero-dates-trigger" className="flex flex-[2] divide-x divide-white/20 border-b border-white/20 md:border-b-0 md:border-r">
              <span className={cell}><span className={label}>{t("w.checkin")}</span><span className="text-sm">{f(b.range.from)}</span></span>
              <span className={cell}><span className={label}>{t("w.checkout")}</span><span className="text-sm">{f(b.range.to)}</span></span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="center" className="w-auto rounded-3xl bg-paper p-6" data-lenis-prevent>
            <BookingCalendar b={b} months={wide ? 2 : 1} />
            <div className="mt-4 flex justify-between">
              <button onClick={b.clear} className="text-sm font-semibold underline underline-offset-4">{t("cal.clear")}</button>
              <button data-testid="hero-calendar-done" onClick={() => setDOpen(false)} className="rounded-xl bg-ink px-5 py-2 text-sm text-paper">{t("w.done")}</button>
            </div>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="hero-guests-trigger" className={`${cell} hidden items-center justify-between border-b border-white/20 md:flex md:border-b-0 md:border-r`}>
              <span><span className={label}>{t("w.guests")}</span><span className="text-sm">{guests} {guests === 1 ? t("l.guest") : t("l.guests")}</span></span>
              <ChevronDown className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] rounded-3xl bg-white p-5"><GuestPicker b={b} /></PopoverContent>
        </Popover>
        <button
          data-testid="hero-book-button"
          onClick={() => (b.ready ? onRequest() : setDOpen(true))}
          className="m-2 rounded-xl bg-paper px-8 py-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold md:rounded-full"
        >
          {t("hb.book")}
        </button>
      </div>
    </motion.div>
  );
};

export const Hero = ({ prop, b, onRequest, site = true }) => {
  const { t, lang } = useI18n();
  const settings = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const textO = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const overline = site ? heroText(settings, lang, "overline") : prop?.location?.area || "";
  const custom = site ? heroText(settings, lang, "lines").split("\n").map((s) => s.trim()).filter(Boolean) : [prop?.name || ""];
  const lines = custom.length ? custom : t("hero.lines");
  const overlay = Number(settings?.hero?.overlay ?? 0.35);
  const heroSize = Number(settings?.theme?.hero_size) || 96;
  const th = settings?.theme || {};
  const ovSize = Number(th.overline_size) || 12;
  const ovSpacing = th.overline_spacing ?? 0.38;
  const ovFont = { mono: "font-mono", display: "font-display", body: "" }[th.overline_font || "mono"];
  const ovUpper = th.overline_upper !== false;
  const titleUpper = th.title_upper !== false;
  const textColor = th.hero_text_color || "#FBF8F2";

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-deep text-paper">
      <motion.div style={{ scale, y }} className="absolute inset-0">
        <Media hero={site ? settings?.hero : { type: "image" }} fallback={prop?.photos?.[0]?.url || "/images/p16.jpg"} />
      </motion.div>
      <div className="absolute inset-0 bg-black" style={{ opacity: overlay }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
      <div className="relative flex h-full flex-col justify-between px-5 pb-24 pt-28 sm:px-8 lg:pb-10">
        <motion.div style={{ y: textY, opacity: textO }} className="flex flex-1 flex-col items-center justify-center text-center">
          <motion.p data-testid="hero-overline" initial={{ opacity: 0, letterSpacing: `${ovSpacing + 0.2}em` }} animate={{ opacity: 1, letterSpacing: `${ovSpacing}em` }} transition={{ delay: 0.2, duration: 1.4 }} style={{ fontSize: `clamp(10px, 3vw, ${ovSize}px)`, color: textColor }} className={`no-scale ${ovFont} ${ovUpper ? "uppercase" : ""} opacity-90`}>
            {overline || t("hero.overline")}
          </motion.p>
          <h1 data-testid="hero-title" style={{ fontSize: `clamp(34px, 11vw, ${heroSize}px)`, color: textColor }} className={`no-scale mt-6 max-w-6xl font-display font-normal leading-[0.95] ${titleUpper ? "uppercase" : ""}`}>
            {Array.isArray(lines) && lines.map((l, i) => <Line key={`${lang}-${i}`} i={i} text={l} />)}
          </h1>
        </motion.div>
        <HeroBar b={b} onRequest={onRequest} />
      </div>
    </section>
  );
};
